/**
 * Плавающий «+» HUB — общий с CRM: вопрос в УК, ошибка, идея, «Мои обращения»
 * (владелец 19.10.2026). Клиент CRM-ручек `/api/feedback*` (тот же origin через nginx,
 * cookie fib.sid), типы и подписи — зеркало frontend/src/lib/feedback.ts и support.ts
 * (импорт между пакетами запрещён). Сотрудник iiko без CRM-аккаунта пишет через
 * `hubApi.sendFeedback` (/api/wiki/hub/feedback) — см. components/feedback.
 */
import { ApiError } from './api';

export type FeedbackKind = 'bug' | 'idea' | 'question';
export type FeedbackStatus = 'new' | 'triage' | 'agreed' | 'progress' | 'verify' | 'done' | 'rejected';
export type FeedbackSeverity = 'block' | 'annoy' | 'minor';

/** Виды обращений: подпись и цвет-токен (CSS-переменная — работает и в тёмной теме). */
export const FB_KINDS: Record<FeedbackKind, { label: string; tone: string }> = {
  bug: { label: 'Ошибка', tone: 'var(--fk-error)' },
  idea: { label: 'Идея', tone: 'var(--fk-success-text)' },
  question: { label: 'Вопрос', tone: 'var(--fk-info)' },
};

/** Статусы CRM: Новое → Разбираемся → (Согласовано) → В работе → На проверке → Готово / Отклонено. */
export const FB_STATUSES: Record<FeedbackStatus, { label: string; tone: string }> = {
  new: { label: 'Новое', tone: 'var(--fk-info)' },
  triage: { label: 'Разбираемся', tone: 'var(--fk-orange-hover)' },
  agreed: { label: 'Согласовано', tone: 'var(--fk-chart-5)' },
  progress: { label: 'В работе', tone: 'var(--fk-chart-6)' },
  verify: { label: 'На проверке у партнёра', tone: 'var(--fk-warning-text)' },
  done: { label: 'Готово', tone: 'var(--fk-success-text)' },
  rejected: { label: 'Отклонено', tone: 'var(--fk-text-muted)' },
};

export const statusOf = (k: string): { label: string; tone: string } => FB_STATUSES[k as FeedbackStatus] ?? FB_STATUSES.new;
/** Подпись статуса для автора: «На проверке у партнёра» → «Проверьте, пожалуйста». */
export const authorStatusLabel = (k: string): string => (k === 'verify' ? 'Проверьте, пожалуйста' : statusOf(k).label);

/** «Насколько мешает» (только ошибка): подписи и подсказки как в CRM. */
export const FB_SEVERITY: Record<FeedbackSeverity, { label: string; hint: string; tone: string }> = {
  block: { label: 'Мешает работать', hint: 'не могу сделать дело', tone: 'var(--fk-error)' },
  annoy: { label: 'Неудобно', hint: 'есть обходной путь', tone: 'var(--fk-orange-hover)' },
  minor: { label: 'Мелочь', hint: 'опечатка, вид, цвет', tone: 'var(--fk-text-muted)' },
};

// ───────── Ответы CRM ─────────

export interface WidgetInfo {
  enabled: boolean;
  texts: { bugIntro: string; ideaIntro: string; slaText: string };
  counts: { verify: number; unread: number; open: number };
  ideasPublic?: boolean;
  isTeam?: boolean;
  /** Вопрос в УК включён. Нет поля (старый бэкенд) — считаем включённым. */
  questionEnabled?: boolean;
}

/**
 * Что показывать в «+»: сотруднику iiko — всё (у него свой путь); CRM-пользователю — по ответу
 * `GET /feedback/widget`. Нет ответа или ошибка — как «включено» (отправка сама скажет, если что не так);
 * `enabled: false` — CRM выключила виджет роли; `questionEnabled: false` — убран только «Вопрос в УК»,
 * нет поля (бэкенд старше) — «Вопрос в УК» есть.
 */
export function feedbackGate(isIiko: boolean, info: Pick<WidgetInfo, 'enabled' | 'questionEnabled'> | undefined): { feedbackOn: boolean; questionOn: boolean } {
  const feedbackOn = isIiko || info?.enabled !== false;
  return { feedbackOn, questionOn: feedbackOn && (isIiko || info?.questionEnabled !== false) };
}

export interface FeedbackEvent {
  id: string;
  type: string;
  internal: boolean;
  authorName: string | null;
  text: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  meta: Record<string, unknown>;
  createdAt: string;
}

export interface FeedbackAttachment {
  id: string;
  kind: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  eventId: string | null;
  createdAt: string;
}

export interface MyTicket {
  id: string;
  code: string;
  kind: FeedbackKind;
  status: FeedbackStatus;
  statusLabel: string;
  title: string;
  section: string | null;
  severity: FeedbackSeverity | null;
  isMine: boolean;
  authorName: string | null;
  authorUnread: boolean;
  rejectReason: string | null;
  createdAt: string;
  updatedAt: string;
  // Только у вопросов в УК: отдел, ответственный, срок ответа.
  deptLabel?: string | null;
  assigneeName?: string | null;
  taken?: boolean;
  dueAt?: string | null;
  firstResponseAt?: string | null;
  urgent?: boolean;
}

export interface MyTicketDetail extends MyTicket {
  body: string | null;
  expected: string | null;
  events: FeedbackEvent[];
  attachments: FeedbackAttachment[];
}

export type RoutedBy = 'keywords' | 'ai' | 'manual' | 'fallback';

/** Ответ `POST /feedback/route-preview` — «Кому уйдёт вопрос». */
export interface RoutingResult {
  deptCode: string;
  deptLabel: string;
  routedBy: RoutedBy;
  hits: string[];
  alternatives: Array<{ code: string; label: string }>;
  ai?: { confidence: number; reason: string };
  urgent: boolean;
  assignee: { id: string; name: string; why: string } | null;
  context: string[];
  slaMinutes: number;
  dueAt: string;
  kb: { title: string; url: string } | null;
}

export interface SupportDepartment {
  code: string;
  label: string;
  hint: string | null;
  slaText: string;
}

export interface CreateFeedbackPayload {
  kind: FeedbackKind;
  title?: string;
  body: string;
  expected?: string;
  severity?: FeedbackSeverity;
  section: string;
  pageUrl: string;
  context?: Record<string, unknown>;
  /** Вопрос в УК: отдел, выбранный вручную («Не туда? Выбрать отдел самому»). */
  deptCode?: string;
}

// ───────── Запросы ─────────

const TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 60_000;

/** Текст ошибки из тела Nest: `message` — строка или список. */
function errorMessage(status: number, parsed: unknown): string {
  if (status === 401) return 'Сессия истекла — обновите страницу и войдите снова';
  if (parsed && typeof parsed === 'object' && 'message' in parsed) {
    const m = (parsed as { message: unknown }).message;
    if (Array.isArray(m)) return m.map(String).join(' · ');
    if (m) return String(m);
  }
  if (status === 429) return 'Слишком много обращений — попробуйте позже';
  if (status === 413) return 'Файл слишком большой';
  return `HTTP ${status}`;
}

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown, opts: { signal?: AbortSignal; timeoutMs?: number } = {}): Promise<T> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), opts.timeoutMs ?? TIMEOUT_MS);
  const onAbort = (): void => ctl.abort();
  if (opts.signal?.aborted) ctl.abort();
  opts.signal?.addEventListener('abort', onAbort);
  try {
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
    const r = await fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers: body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {},
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      signal: ctl.signal,
    });
    if (!r.ok) {
      let parsed: unknown = null;
      try {
        parsed = await r.json();
      } catch {
        /* не JSON */
      }
      throw new ApiError(r.status, parsed, errorMessage(r.status, parsed));
    }
    if (r.status === 204) return undefined as T;
    return (await r.json()) as T;
  } finally {
    clearTimeout(timer);
    opts.signal?.removeEventListener('abort', onAbort);
  }
}

export const crmFeedbackApi = {
  widget: () => call<WidgetInfo>('GET', '/feedback/widget'),
  my: () => call<MyTicket[]>('GET', '/feedback/my'),
  myOne: (id: string) => call<MyTicketDetail>('GET', `/feedback/my/${encodeURIComponent(id)}`),
  create: (p: CreateFeedbackPayload) => call<{ id: string; code: string }>('POST', '/feedback', p),
  comment: (id: string, text: string) => call<unknown>('POST', `/feedback/${encodeURIComponent(id)}/comments`, { text }),
  attach: (id: string, file: Blob, filename: string, kind: 'screenshot' | 'file') => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file, filename);
    return call<FeedbackAttachment>('POST', `/feedback/${encodeURIComponent(id)}/attachments`, fd, { timeoutMs: UPLOAD_TIMEOUT_MS });
  },
  /** «Кому уйдёт вопрос». Ничего не сохраняет; `signal` — отмена устаревшего запроса. */
  routePreview: (body: { text: string; deptCode?: string }, signal?: AbortSignal) => call<RoutingResult>('POST', '/feedback/route-preview', body, { signal }),
  departments: () => call<SupportDepartment[]>('GET', '/feedback/support/departments'),
};

/** Адрес файла вложения (картинка / PDF открываются в браузере). */
export const feedbackFileUrl = (ticketId: string, attId: string): string => `/api/feedback/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attId)}`;

// ───────── Подписи ─────────

/** «3 ч назад», «вчера», «5 дн назад» — как в CRM. */
export function ago(iso: string, now: number = Date.now()): string {
  const h = Math.round((now - new Date(iso).getTime()) / 3600000);
  if (h < 1) return 'только что';
  if (h < 24) return `${h} ч назад`;
  const d = Math.round(h / 24);
  return d === 1 ? 'вчера' : `${d} дн назад`;
}

/** «4 окт 16:05». */
export function fmtDT(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Подпись записи ленты: «сменил(а) статус: В работе», «ответил(а) партнёру»… */
export function eventWhat(e: FeedbackEvent, kind?: FeedbackKind): string {
  switch (e.type) {
    case 'created':
      return e.meta?.manual ? 'внёс(ла) обращение со слов партнёра' : `отправил(а) ${(kind ? FB_KINDS[kind].label : 'обращение').toLowerCase()}`;
    case 'status':
      return `статус: ${statusOf(e.toStatus ?? '').label}${e.meta?.auto ? ' (после первого ответа)' : ''}`;
    case 'reply':
      return 'ответил(а) партнёру';
    case 'note':
      return 'заметка для команды';
    case 'comment':
      return 'написал(а)';
    case 'assign':
      return 'изменил(а) свойства';
    case 'merge':
      return 'объединение';
    case 'system':
      return 'определила отдел и срок';
    case 'verify_ok':
      return 'проверил(а): всё работает';
    case 'verify_fail':
      return 'проверил(а): не исправлено';
    default:
      return 'событие';
  }
}

// ───────── Файлы ─────────

export const FEEDBACK_FILE_MAX_MB = 10;
export const FEEDBACK_FILES_MAX = 5;
const ALLOWED_FILE = /^image\/(png|jpeg|webp)$|^application\/pdf$/;

/** Проверка файла перед отправкой: текст ошибки или null. */
export function fileProblem(f: { name: string; type: string; size: number }, maxMb: number = FEEDBACK_FILE_MAX_MB): string | null {
  if (!ALLOWED_FILE.test(f.type)) return `«${f.name}»: можно только картинку (PNG, JPG, WEBP) или PDF`;
  if (f.size > maxMb * 1024 * 1024) return `«${f.name}» больше ${String(maxMb).replace('.', ',')} МБ`;
  return null;
}

/** Раздел обращения на доске CRM: «HUB · <страница>» (бэкенд отбирает HUB-обращения по префиксу). Длина — как у колонки (160). */
export const crmSection = (page: string): string => `HUB · ${page.replace(/\s+/g, ' ').trim() || 'Без раздела'}`.slice(0, 160);

/** Первая строка текста — тема обращения (так же, как делает бэкенд, если темы нет). */
export const titleFromBody = (body: string): string => body.trim().split('\n')[0].slice(0, 120);
